interface PaidPaymentInput {
  type?: string;
  state: string;
  captured?: boolean;
  capturedAt?: string | null;
  cardInfo?: { last4?: string } | null;
}

/**
 * Whether a payment is Buy Now, Pay Later (Klarna/Affirm/Afterpay). On the
 * backend BNPL is a regular card charge captured at booking time, so it has
 * state 'succeeded' + captured. The only distinguishing signal is
 * `cardInfo.last4 === 'BNPL'` (this matches how the backend's SaleOrder.getBNPL
 * detects it). A BNPL booking is captured on the merchant side but the customer
 * still owes the installments, so it should read as pending, not paid.
 */
export const isBnplPayment = (cardInfo?: { last4?: string } | null): boolean =>
  cardInfo?.last4 === 'BNPL';

export const isGiftCardPaymentMethod = (paymentMethod?: string): boolean =>
  paymentMethod?.toLowerCase().replace(/[\s_-]/g, '') === 'giftcard';

/**
 * Whether a charge has been captured (settled).
 *
 * The customer-sale API reports a captured card payment as state 'succeeded'
 * (there is no 'processed' payment state — that's the sale order's state). A
 * 'succeeded' charge is only captured once `captured`/`capturedAt` is set; an
 * authorized-but-not-captured charge (Stripe requires_capture) is succeeded
 * but not yet captured.
 */
export const isPaymentCaptured = (
  captured?: boolean,
  capturedAt?: string | null,
): boolean => captured === true || Boolean(capturedAt);

/**
 * Whether a payment should be shown as PAID. True when the sale order already
 * reports the payment as 'processed', or when the card charge has succeeded
 * and been captured. BNPL is always treated as pending (see isBnplPayment).
 * A gift card is never captured, so a succeeded one counts as paid.
 */
export const isPaymentPaid = (payment: PaidPaymentInput): boolean => {
  if (isBnplPayment(payment.cardInfo)) {
    return false;
  }
  if (payment.state === 'processed') {
    return true;
  }
  if (isGiftCardPaymentMethod(payment.type)) {
    return payment.state === 'succeeded';
  }
  return (
    payment.state === 'succeeded' &&
    isPaymentCaptured(payment.captured, payment.capturedAt)
  );
};

/**
 * The payment PaymentStatus shows for a sale. A gift card is the payment
 * method only when it paid the whole order; next to a card it stays a
 * breakdown line, so the card is shown instead (matches ios-flagship).
 */
export const getDisplayedPayment = <T extends { type: string }>(
  payments: T[],
): T | undefined =>
  payments.find((p) => !isGiftCardPaymentMethod(p.type)) ?? payments[0];
